import type { ReactNode } from 'react';
import { cx } from '@client/shared/lib/cx.ts';
import { AnchoredCard } from './AnchoredCard.tsx';
import type { CardAnchor, CardPlacement } from './AnchoredCard.tsx';
import { Button } from './Button.tsx';
import { Eyebrow } from './Text.tsx';
import styles from './CoachMark.module.css';

export interface CoachMarkProps {
  /** The UI this note points at (an element ref or a screen point). */
  anchor: CardAnchor;
  /** Which side of the anchor the tooltip sits on. Default `'bottom'`. */
  placement?: CardPlacement;
  /** e.g. "1 of 2" — shown only when the mark is one step of a sequence. */
  step?: string;
  dismissLabel?: string;
  onDismiss: () => void;
  children: ReactNode;
}

/**
 * A first-run tutorial tooltip that floats beside the UI it explains, pointing at it
 * with an arrow. At most one per screen; the tutorial ends via onDismiss (skip / got it)
 * or when the user completes the flow it teaches.
 */
export function CoachMark({
  anchor,
  placement = 'bottom',
  step,
  dismissLabel = 'Skip tour',
  onDismiss,
  children,
}: CoachMarkProps) {
  return (
    <AnchoredCard
      anchor={anchor}
      arrow
      role="status"
      gap={10}
      className={styles.coachMark}
      placement={placement}
    >
      <div className={styles.header}>
        <span className={styles.beacon} aria-hidden />
        <Eyebrow className={styles.eyebrow}>Getting started</Eyebrow>
        {step && <span className={cx(styles.step, 'tnum')}>{step}</span>}
      </div>
      <div className={styles.body}>{children}</div>
      <div className={styles.footer}>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          {dismissLabel}
        </Button>
      </div>
    </AnchoredCard>
  );
}
